import { Button, Checkbox, Stack, Tag, TagCloseButton, TagLabel, Text, useDisclosure } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { formatAddress, PREFERRED_ORIGIN_GATEWAYS } from '@pelicargo/utils';
import { sortBy } from 'lodash';
import { RefObject, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { InstantSearch, useHits } from 'react-instantsearch-core';
import usePlacesAutocomplete, { getGeocode, getLatLng, Suggestion } from 'use-places-autocomplete';

import { useTypesense } from '../../hooks/useTypesense';
import { AddressForm } from '../Forms/AddressForm';
import { Search } from './Search';

type HitProps = { isAddressSelected: boolean };

type AirportHit = {
  airport_id: string;
  geo_distance_meters?: {
    airport_latlng: {
      lat: number;
      lng: number;
    };
  };
};

const Hits = ({ isAddressSelected }: HitProps) => {
  const { setValue } = useFormContext();
  const { hits } = useHits<AirportHit>();

  const selectedAirportId = useMemo(() => {
    if (!isAddressSelected || !hits?.length) return '';

    const filteredHits = hits.filter((hit) => !!hit?.geo_distance_meters?.airport_latlng);

    const sortedHits = sortBy(filteredHits, 'geo_distance_meters.airport_latlng');
    const preferredHits = sortedHits.filter((hit) =>
      PREFERRED_ORIGIN_GATEWAYS.some((gateway) => gateway.id === hit.airport_id),
    );

    return (preferredHits[0] ?? sortedHits[0])?.airport_id ?? '';
  }, [hits, isAddressSelected]);

  useEffect(() => {
    if (selectedAirportId) setValue('origin_airports', selectedAirportId ? [selectedAirportId] : []);
  }, [selectedAirportId, setValue]);

  return null;
};

type ClosestAirportPickerProps = {
  onClose: () => void;
  addressField: string;
  disableManualInput?: boolean;
  allowAll?: boolean; // Shows international addresses
  initialFocusRef?: RefObject<HTMLInputElement>;
};

export const ClosestAirportPicker = ({
  onClose,
  addressField,
  disableManualInput = false,
  allowAll,
  initialFocusRef,
}: ClosestAirportPickerProps) => {
  const { control, setValue } = useFormContext();

  const selectedAddress = useWatch({ control, name: addressField });
  const { isOpen: shouldShowAddressForm, onToggle: toggleAddressForm } = useDisclosure();

  const addressName = useMemo(() => formatAddress(selectedAddress), [selectedAddress]);

  const {
    suggestions,
    value: query,
    setValue: setQuery,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: allowAll ? undefined : { componentRestrictions: { country: ['US'] } },
  });

  const sort_by = useMemo(
    () =>
      selectedAddress?.latitude && selectedAddress?.longitude
        ? `airport_main_gateway:desc,airport_latlng(${selectedAddress?.latitude}, ${selectedAddress?.longitude}):asc`
        : 'airport_main_gateway:desc,airport_origin_popularity:desc',
    [selectedAddress],
  );

  const filter_by = useMemo(
    () =>
      `${allowAll ? '' : 'airport_country:=US'}${
        selectedAddress?.latitude && selectedAddress?.longitude
          ? ` && airport_latlng:(${selectedAddress?.latitude}, ${selectedAddress?.longitude}, 2000 mi)`
          : ''
      }`,
    [allowAll, selectedAddress],
  );

  const typesense = useTypesense({
    additionalSearchParameters: {
      query_by: 'airport_id,airport_name',
      sort_by,
      filter_by,
      per_page: 20,
    },
  } as any);

  const deselect = () => {
    setValue(addressField, null);
    setValue('origin_airports', []);
  };

  const handlePredictionChange = (prediction) => async () => {
    const result = (await getGeocode({ address: prediction?.description }))?.[0];
    const { lat: latitude, lng: longitude } = getLatLng(result);

    const getAddressComponent = (type: string) =>
      result.address_components?.find(({ types }) => types?.includes(type))?.short_name ?? '';

    const line_one = [getAddressComponent('street_number'), getAddressComponent('route')].filter(Boolean).join(' ');
    const line_two = getAddressComponent('subpremise');
    const zip_code = getAddressComponent('postal_code');
    const city = getAddressComponent('locality');
    const state = getAddressComponent('administrative_area_level_1');
    const country = getAddressComponent('country');

    setValue(addressField, {
      recipient: prediction?.structured_formatting?.main_text || '',
      line_one,
      line_two,
      country,
      state,
      city,
      zip_code,
      latitude,
      longitude,
      place_id: result?.place_id,
    });
    onClose();
  };

  const handleAddressChange = async () => {
    const result = (await getGeocode({ address: addressName }))?.[0];
    const latlng = getLatLng(result);

    setValue(addressField, {
      recipient: selectedAddress?.recipient,
      line_one: selectedAddress?.line_one,
      line_two: selectedAddress?.line_two,
      country: selectedAddress?.country,
      state: selectedAddress?.state,
      city: selectedAddress?.city,
      zip_code: selectedAddress?.zip_code,
      latitude: latlng?.lat,
      longitude: latlng?.lng,
    });
  };

  const renderPrediction = (prediction: Suggestion) => (
    <Checkbox
      key={prediction?.place_id}
      size="md"
      colorScheme="primary"
      whiteSpace="nowrap"
      onChange={handlePredictionChange(prediction)}
    >
      {prediction?.description}
    </Checkbox>
  );

  if (!typesense?.searchClient) return 'Loading ...';

  return (
    <InstantSearch searchClient={typesense.searchClient} indexName="airports">
      <Stack maxW="none" pb="4" mt="4">
        <Show if={!!addressName}>
          <Tag size="sm" borderRadius="full" variant="outline" color="offBlack">
            <TagLabel>{addressName}</TagLabel>
            <TagCloseButton onClick={deselect} />
          </Tag>
        </Show>
        <Stack direction="row" justifyContent="flex-end" pr={shouldShowAddressForm ? '0' : '4'}>
          <Show if={!shouldShowAddressForm}>
            <Search
              query={query}
              placeholder="Where from?"
              setQuery={setQuery}
              initialFocusRef={initialFocusRef}
              refineSearch={false}
            />
          </Show>
          <Show if={!disableManualInput}>
            <Button borderRadius="md" size="xs" px={shouldShowAddressForm ? '2' : '4'} onClick={toggleAddressForm}>
              {shouldShowAddressForm ? 'Address Search' : 'Manual Input'}
            </Button>
          </Show>
        </Stack>
        <Show if={!shouldShowAddressForm}>
          <Show if={!addressName}>
            <Show if={!suggestions?.data?.length}>
              <Text fontSize="sm" color="text.placeholder" w="md">
                {query ? 'No results found' : 'Search by address, city, or zip'}
              </Text>
            </Show>
            {suggestions?.data?.map(renderPrediction)}
          </Show>
          <Show if={!!addressName}>
            <Checkbox
              key={selectedAddress?.place_id}
              size="md"
              colorScheme="primary"
              isChecked
              whiteSpace="nowrap"
              onChange={deselect}
            >
              {addressName}
            </Checkbox>
          </Show>
        </Show>
        <Hits isAddressSelected={!!addressName} />
        <Show if={shouldShowAddressForm}>
          <AddressForm name={addressField} handleAddressChange={handleAddressChange} />
        </Show>
      </Stack>
    </InstantSearch>
  );
};
